import { TurnstileComponent } from '@/lib';
import {
  requestPasswordResetRequest,
  resetPasswordRequest,
} from '@/lib/recepthor-api';
import { Button, Input } from '@/ui';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import BrandPane from './BrandPane.jsx';
import { validatePassword } from './passwordValidation';

function RequestResetForm({ loginTo }) {
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Completa el captcha antes de continuar.');
      return;
    }

    setLoading(true);

    try {
      const response = await requestPasswordResetRequest({
        email,
        captchaToken,
      });

      // Treat 404 as success to prevent email enumeration.
      if (response.ok || response.status === 404) {
        setSubmitted(true);
        return;
      }

      setError('Error al procesar la solicitud. Intenta nuevamente.');
      // El token se consumió con el intento (es de un solo uso): token fresco
      // para el reintento.
      setCaptchaToken('');
      captchaRef.current?.reset();
    } catch {
      setError('Error de conexión. Verifica tu internet e intenta nuevamente.');
      setCaptchaToken('');
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-form-pane">
        <div className="login-form">
          <p className="login-eyebrow">Recuperación</p>
          <h1 className="login-heading">Revisa tu correo</h1>
          <p className="recover-subtitle">
            Si el correo está registrado, recibirás un enlace para restablecer
            tu contraseña.
          </p>
          <Link to={loginTo} className="login-forgot">
            Volver a inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-form-pane">
      <form className="login-form" onSubmit={handleSubmit}>
        <p className="login-eyebrow">Recuperación</p>
        <h1 className="login-heading">Recupera tu contraseña</h1>
        <p className="recover-subtitle">
          Ingresa tu correo electrónico y te enviaremos un enlace para
          restablecerla.
        </p>
        <div className="login-fields">
          <div className="login-field">
            <label className="login-label" htmlFor="recover-email">
              Correo electrónico *
            </label>
            <Input
              id="recover-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <TurnstileComponent ref={captchaRef} onVerify={setCaptchaToken} />
          {error && <p className="login-alert login-alert--err">{error}</p>}
          <Button
            type="submit"
            variant="seal"
            size="lg"
            disabled={loading || !captchaToken}
            className="login-submit"
          >
            {loading ? 'Enviando…' : 'Enviar enlace'}
          </Button>
        </div>
        <Link to={loginTo} className="login-forgot">
          Volver a inicio de sesión
        </Link>
      </form>
    </div>
  );
}

RequestResetForm.propTypes = {
  loginTo: PropTypes.string.isRequired,
};

function ResetPasswordForm({ token }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      const response = await resetPasswordRequest({ token, password });

      if (response.ok) {
        navigate('/login', { state: { passwordReset: true } });
        return;
      }

      if (response.status === 400 || response.status === 404) {
        setError(
          'El enlace es inválido o ha expirado. Solicita uno nuevo desde la página de recuperación.',
        );
      } else {
        setError('Error al restablecer la contraseña. Intenta nuevamente.');
      }
    } catch {
      setError('Error de conexión. Verifica tu internet e intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-form-pane">
      <form className="login-form" onSubmit={handleSubmit}>
        <p className="login-eyebrow">Recuperación</p>
        <h1 className="login-heading">Nueva contraseña</h1>
        <p className="recover-subtitle">
          Crea una nueva contraseña para tu cuenta.
        </p>
        <div className="login-fields">
          <div className="login-field">
            <label className="login-label" htmlFor="reset-password">
              Nueva contraseña *
            </label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="login-field">
            <label className="login-label" htmlFor="reset-confirm">
              Confirmar contraseña *
            </label>
            <Input
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="login-alert login-alert--err">{error}</p>}
          <Button
            type="submit"
            variant="seal"
            size="lg"
            disabled={loading}
            className="login-submit"
          >
            {loading ? 'Restableciendo…' : 'Restablecer contraseña'}
          </Button>
        </div>
        <Link to="/recuperar-contrasena" className="login-forgot">
          Solicitar nuevo enlace
        </Link>
      </form>
    </div>
  );
}

ResetPasswordForm.propTypes = {
  token: PropTypes.string.isRequired,
};

export default function RecoverPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // Quien llegó desde el login de receptores vuelve a ese mismo login.
  const loginTo =
    searchParams.get('portal') === 'receptor'
      ? '/login?portal=receptor'
      : '/login';

  return (
    <div className="login-page">
      <BrandPane />
      {token ? (
        <ResetPasswordForm token={token} />
      ) : (
        <RequestResetForm loginTo={loginTo} />
      )}
    </div>
  );
}
